import { FC, Fragment, ReactNode, useEffect, useRef } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'

import { AnalyticsEvent } from 'utils/analytics/AnalyticsEvent'
import LoadingPage from 'components/pages/General/LoadingPage/LoadingPage'
import { Path } from 'constants/router'
import { logAnalyticsEvent } from 'utils/analytics'
import { useAuth0 } from 'utils/auth'
import { useWorkspaceDeepLink } from 'utils/hooks/useWorkspaceDeepLink'

/** Query param carrying the linked workspace id, e.g. `/workspace-settings/members?workspaceId=…`. */
export const WORKSPACE_DEEP_LINK_PARAM = 'workspaceId'

/** Query param naming where the link came from; absent means a backend email. */
export const WORKSPACE_DEEP_LINK_SOURCE_PARAM = 'source'

/** Where a workspace deep link came from: a backend email, or an old `/workspaces-profile/:id` link forwarded here. */
export type WorkspaceDeepLinkSource = 'email' | 'legacy_url'

/** The settings page a workspace deep link targets, and the page it lands on. */
export type WorkspaceDeepLinkPage = 'members' | 'profile'

interface Props {
  /** Which settings page this wrapper guards; `members` also enforces who may see the Members tab. */
  page: WorkspaceDeepLinkPage
  children: ReactNode
}

/**
 * Lets a settings page own its deep links. Backend emails about a workspace (a member left or was removed,
 * a role or access change) link to `/workspace-settings/members?workspaceId={id}` or
 * `/profile/settings?workspaceId={id}`, and the page must open on THAT workspace, not on whichever one the
 * tab happens to have selected.
 *
 * Without `?workspaceId=` — and for a signed-out user, whom the page sends to the login screen — the
 * children render untouched; the only cost is reading the search params. With it, the page is held on
 * {@link LoadingPage} while {@link useWorkspaceDeepLink} switches the tab into the linked workspace, then:
 *
 * - `workspaceId` and `source` are removed from the URL with `replace`, every other param (UTM…) kept;
 * - on the `members` page, a user who may not see the linked workspace's Members — not an admin, a
 *   sponsored seat, or no linked workspace at all (not a member, embed-pinned, timed out) — is sent to
 *   profile settings instead, rather than being bounced to the dashboard by the workspace settings gates;
 * - one `WORKSPACE_DEEP_LINK_RESOLVED` event reports the outcome, the page the link targeted (`page`), the
 *   page the user ended up on (`landedPage`) and the source.
 *
 * Mount it ABOVE the page's own gates (`Workspace`, `WorkspacePageController`), which check the selected
 * workspace and would otherwise judge the previous one.
 */
export const WorkspaceDeepLink: FC<Props> = ({ page, children }) => {
  const [searchParams] = useSearchParams()
  const { isAuthenticated } = useAuth0()

  const workspaceId = searchParams.get(WORKSPACE_DEEP_LINK_PARAM)
  if (workspaceId === null || !isAuthenticated) return <Fragment>{children}</Fragment>

  const source: WorkspaceDeepLinkSource = searchParams.get(WORKSPACE_DEEP_LINK_SOURCE_PARAM) === 'legacy_url' ? 'legacy_url' : 'email'

  // Keyed on the link, so following a second deep link while one is resolving starts over.
  return <WorkspaceDeepLinkResolver key={workspaceId} page={page} workspaceId={workspaceId} source={source} />
}

interface ResolverProps {
  page: WorkspaceDeepLinkPage
  workspaceId: string
  source: WorkspaceDeepLinkSource
}

/** Resolves one deep link, then replaces the URL with the page to land on, minus the deep-link params. */
const WorkspaceDeepLinkResolver: FC<ResolverProps> = ({ page, workspaceId, source }) => {
  const location = useLocation()
  const { isResolving, outcome, canSeeMembers } = useWorkspaceDeepLink(workspaceId)

  const landedPage: WorkspaceDeepLinkPage = page === 'members' && !canSeeMembers ? 'profile' : page
  const pathname = landedPage === page ? location.pathname : Path.PROFILE_SETTINGS

  const hasReportedRef = useRef(false)
  useEffect(() => {
    if (isResolving || hasReportedRef.current) return
    hasReportedRef.current = true

    logAnalyticsEvent(AnalyticsEvent.WORKSPACE_DEEP_LINK_RESOLVED, { outcome, page, landedPage, linkedWorkspaceId: workspaceId, source })
  }, [isResolving, outcome, page, landedPage, workspaceId, source])

  if (isResolving) return <LoadingPage />

  const search = new URLSearchParams(location.search)
  search.delete(WORKSPACE_DEEP_LINK_PARAM)
  search.delete(WORKSPACE_DEEP_LINK_SOURCE_PARAM)
  const remainingSearch = search.toString()

  return (
    <Navigate
      replace
      to={{ pathname, search: remainingSearch ? `?${remainingSearch}` : '', hash: location.hash }}
      state={location.state}
    />
  )
}
